import React, { useEffect, useRef, useState } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';
import { api } from '../../api/client';
import { getThumbnailVersion } from '../../hooks/useThumbnailCapture';
import { useTheme } from '../../contexts/ThemeContext';

const useStyles = makeStyles({
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
});

interface DashboardThumbnailProps {
  dashboardId: string;
}

/**
 * Dashboard thumbnail — always tries to load the real thumbnail via API.
 * Falls back to a placeholder SVG silhouette on 404/error.
 */
const DashboardThumbnail: React.FC<DashboardThumbnailProps> = ({ dashboardId }) => {
  const styles = useStyles();
  const [imgError, setImgError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { isDarkMode } = useTheme();

  const mode = isDarkMode ? 'dark' : 'light';
  const version = getThumbnailVersion(dashboardId);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [dashboardId]);

  useEffect(() => {
    if (!isVisible) return;
    let active = true;
    let objectUrl: string | null = null;
    setImgError(false);
    setImageUrl(null);

    api.getBlob(`/dashboards/${dashboardId}/thumbnail?mode=${mode}&t=${version}`, {
      suppressErrorCapture: true,
    }).then((blob) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setImageUrl(objectUrl);
    }).catch(() => {
      if (active) setImgError(true);
    });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [dashboardId, isVisible, mode, version]);

  const showImage = imageUrl && !imgError;

  return (
    <div ref={containerRef} className={styles.container}>
      {showImage ? (
        <img
          src={imageUrl}
          alt="Dashboard preview"
          className={styles.image}
          loading="lazy"
          onError={() => setImgError(true)}
        />
      ) : (
        <svg width="100%" height="100%" viewBox="0 0 400 225" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice">
          {/* KPI block 1 */}
          <rect x="14" y="14" width="113" height="58" rx="8" fill={tokens.colorNeutralStroke2} opacity="0.4" />
          <rect x="24" y="28" width="54" height="11" rx="3" fill={tokens.colorNeutralForeground4} opacity="0.5" />
          <rect x="24" y="46" width="30" height="8" rx="2" fill={tokens.colorNeutralForeground4} opacity="0.25" />

          {/* KPI block 2 */}
          <rect x="143" y="14" width="113" height="58" rx="8" fill={tokens.colorNeutralStroke2} opacity="0.4" />
          <rect x="153" y="28" width="47" height="11" rx="3" fill={tokens.colorNeutralForeground4} opacity="0.5" />
          <rect x="153" y="46" width="24" height="8" rx="2" fill={tokens.colorNeutralForeground4} opacity="0.25" />

          {/* KPI block 3 */}
          <rect x="272" y="14" width="113" height="58" rx="8" fill={tokens.colorNeutralStroke2} opacity="0.35" />
          <rect x="282" y="28" width="40" height="11" rx="3" fill={tokens.colorNeutralForeground4} opacity="0.4" />
          <rect x="282" y="46" width="27" height="8" rx="2" fill={tokens.colorNeutralForeground4} opacity="0.2" />

          {/* Sparkline */}
          <path
            d="M20 175 L60 138 L95 152 L130 116 L165 130 L190 102"
            stroke={tokens.colorBrandStroke1}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            opacity="0.55"
          />
          <path
            d="M20 175 L60 138 L95 152 L130 116 L165 130 L190 102 L190 197 L20 197 Z"
            fill={tokens.colorBrandBackground2}
            opacity="0.18"
          />

          {/* Mini bar chart */}
          <rect x="222" y="160" width="28" height="37" rx="5" fill={tokens.colorBrandBackground2} opacity="0.6" />
          <rect x="260" y="124" width="28" height="73" rx="5" fill={tokens.colorBrandBackground2} opacity="0.75" />
          <rect x="298" y="138" width="28" height="59" rx="5" fill={tokens.colorBrandBackground2} opacity="0.55" />
          <rect x="336" y="109" width="28" height="88" rx="5" fill={tokens.colorBrandBackground2} opacity="0.7" />

          <line x1="14" y1="197" x2="386" y2="197" stroke={tokens.colorNeutralStroke3} strokeWidth="1" opacity="0.35" />
        </svg>
      )}
    </div>
  );
};

export default DashboardThumbnail;